/**
 * useRedemptionActions Hook - Coordinator Pattern Implementation
 *
 * This hook coordinates coupon redemption workflow actions:
 * - Redeem coupons with validation
 * - Process QR codes for redemption
 * - Validate redemption eligibility
 *
 * Pattern: COORDINATOR
 * - Orchestrates redemption mutations
 * - Returns Promises (no data storage)
 * - Loading states per action
 * - No computed values or aggregation
 *
 * IMPORTANT: This hook manages redemption actions only.
 * For redemption data/history, use the useRedemptionState aggregator hook.
 */

import { useCreateRedemption } from '@/generated/hooks';
import type { CreateRedemptionBodyType, CreateRedemptionResponseType } from '@zlinkx/shared-v2';
import { dollarsToCents } from '@zlinkx/shared-v2';
import { Numeric } from '@/utils/numeric';
import type {
	QRCodeParseResult,
	RedemptionActions,
	UseRedemptionActionsOptions,
	UseRedemptionActionsReturn,
} from '@/types/coordinator.types';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Re-export types for convenience
 */
export type { QRCodeParseResult, RedemptionActions, UseRedemptionActionsOptions, UseRedemptionActionsReturn };

// ============================================================================
// HELPER FUNCTIONS (Pure)
// ============================================================================

/**
 * Parse QR code to extract redemption data
 * Supports multiple QR code formats:
 * - Simple: "COUPON_CODE"
 * - Structured: "COUPON:CODE:USER:123"
 * - URL: "https://app.com/redeem/CODE"
 */
function parseQRCode(qrCode: string): QRCodeParseResult {
	if (!qrCode || qrCode.trim().length === 0) {
		return { isValid: false, error: 'QR code is required' };
	}

	const trimmed = qrCode.trim();

	// Check for structured format: COUPON:CODE:USER:ID
	if (trimmed.startsWith('COUPON:')) {
		const parts = trimmed.split(':');
		if (parts.length >= 2) {
			const couponCode = parts[1];
			const userId = parts.length >= 4 ? Numeric.parseInt(parts[3], 0) : undefined;

			if (!couponCode) {
				return { isValid: false, error: 'Invalid QR code format: missing coupon code' };
			}

			return {
				isValid: true,
				couponCode,
				userId: userId !== undefined && Number.isFinite(userId) && userId > 0 ? userId : undefined,
			};
		}
	}

	// Check for URL format
	if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
		try {
			const url = new URL(trimmed);
			const pathMatch = url.pathname.match(/\/redeem\/([A-Z0-9_-]+)/i);
			if (pathMatch !== null) {
				const userId = url.searchParams.get('user');
				const parsedUserId = userId !== null && userId.length > 0 ? Numeric.parseInt(userId, 0) : undefined;
				return {
					isValid: true,
					couponCode: pathMatch[1],
					userId:
						parsedUserId !== undefined && Number.isFinite(parsedUserId) && parsedUserId > 0
							? parsedUserId
							: undefined,
				};
			}
		} catch {
			// Invalid URL, treat as simple code
		}
	}

	// Validate as simple coupon code
	if (/^[A-Z0-9_-]+$/i.test(trimmed)) {
		return { isValid: true, couponCode: trimmed };
	}

	return { isValid: false, error: 'Invalid QR code format' };
}

/**
 * Generated hooks return unwrapped payloads; wrap for existing coordinator/public type.
 */
function wrapRedemptionResponse(
	data: CreateRedemptionResponseType['data'],
): CreateRedemptionResponseType {
	return {
		success: true,
		timestamp: data.timestamp,
		requestId: 'generated-hook',
		data,
	};
}

// ============================================================================
// HOOK IMPLEMENTATION
// ============================================================================

/**
 * Coordinates coupon redemption actions
 *
 * @example
 * ```tsx
 * function RedemptionPage() {
 *   const redemption = useRedemptionActions({
 *     onRedeemSuccess: (result) => {
 *       toast.success('Redemption successful!');
 *       // Update UI or navigate
 *     },
 *     onRedeemError: (error) => {
 *       toast.error('Redemption failed: ' + error.message);
 *     },
 *   });
 *
 *   const handleQRScan = async (qrCode: string) => {
 *     // Parse QR code first
 *     const parsed = redemption.parseQRCode(qrCode);
 *     if (!parsed.isValid) {
 *       toast.error(parsed.error);
 *       return;
 *     }
 *
 *     // Validate before redemption (optional)
 *     try {
 *       const validation = await redemption.validateCoupon(
 *         parsed.couponCode!,
 *         parsed.userId
 *       );
 *
 *       if (!validation.isValid) {
 *         toast.error(validation.message);
 *         return;
 *       }
 *     } catch (error) {
 *       // Validation failed
 *     }
 *
 *     // Process redemption
 *     try {
 *       await redemption.processQRCode(qrCode, {
 *         purchaseAmount: 50.00,
 *         notes: 'In-store purchase',
 *       });
 *     } catch (error) {
 *       // Error handled by onRedeemError
 *     }
 *   };
 *
 *   return (
 *     <div>
 *       <QRScanner
 *         onScan={handleQRScan}
 *         disabled={redemption.isLoading.processQRCode}
 *       />
 *     </div>
 *   );
 * }
 * ```
 */
export function useRedemptionActions(options: UseRedemptionActionsOptions = {}): UseRedemptionActionsReturn {
	const { onRedeemSuccess, onRedeemError } = options;

	// ========================================
	// Generated Hooks (API layer)
	// ========================================

	const redeemMutation = useCreateRedemption({
		onSuccess: data => {
			// Call custom success handler
			onRedeemSuccess?.(wrapRedemptionResponse(data));
		},
		onError: error => {
			// Call custom error handler
			onRedeemError?.(error);
		},
	});

	// Note: validateCoupon doesn't exist in generated hooks yet
	// Validation feature removed until API supports it

	// ========================================
	// Action Functions (Pure, Stateless)
	// ========================================

	/**
	 * Redeem a coupon (for merchants)
	 */
	const redeemCoupon = async (merchantId: number, data: CreateRedemptionBodyType): Promise<CreateRedemptionResponseType> => {
		const response = await redeemMutation.mutateAsync({
			merchantId,
			data,
		});
		return wrapRedemptionResponse(response);
	};

	/**
	 * Process QR code and redeem
	 */
	const processQRCode = async (
		merchantId: number,
		qrCode: string,
		billAmount: number,
		invoiceId: string,
		notes?: string,
	): Promise<CreateRedemptionResponseType> => {
		// Parse QR code
		const parsed = parseQRCode(qrCode);

		if (!parsed.isValid) {
			throw new Error(parsed.error ?? 'Invalid QR code');
		}

		if (parsed.couponCode === undefined || parsed.couponCode.length === 0) {
			throw new Error('Invalid QR code: missing coupon code');
		}

		// Prepare redemption data - convert dollars to cents
		const apiData: CreateRedemptionBodyType = {
			cashier_code: '0000',
			coupon_code: parsed.couponCode,
			purchase_amount: dollarsToCents(billAmount),
			apply_credit: false,
			invoice_id: invoiceId.trim(),
			note: typeof notes === 'string' ? notes.trim() : '',
		};

		// Execute redemption
		const response = await redeemMutation.mutateAsync({
			merchantId,
			data: apiData,
		});
		return wrapRedemptionResponse(response);
	};

	// Note: validateCoupon removed as the API doesn't support it yet

	// ========================================
	// Return Coordinator Pattern
	// ========================================
	return {
		// Actions (return Promises or values)
		redeemCoupon,
		processQRCode,
		parseQRCode,

		// Loading states per action (only for async operations)
		isLoading: {
			redeemCoupon: redeemMutation.isPending,
			processQRCode: redeemMutation.isPending, // Same mutation
			parseQRCode: false,
		},
	};
}

// ============================================================================
// USAGE DOCUMENTATION
// ============================================================================

/**
 * Usage Examples:
 *
 * 1. Direct redemption:
 * ```tsx
 * const redemption = useRedemptionActions();
 *
 * await redemption.redeemCoupon({
 *   qrCode: 'SUMMER2024',
 *   userId: 123,
 *   purchaseAmount: 99.99,
 *   notes: 'Online purchase',
 * });
 * ```
 *
 * 2. QR code scanning workflow:
 * ```tsx
 * const redemption = useRedemptionActions();
 *
 * // Parse QR code
 * const parsed = redemption.parseQRCode(scannedCode);
 * if (!parsed.isValid) {
 *   showError(parsed.error);
 *   return;
 * }
 *
 * // Optional: Validate first
 * const validation = await redemption.validateCoupon(
 *   parsed.couponCode,
 *   parsed.userId
 * );
 *
 * // Process redemption
 * const result = await redemption.processQRCode(scannedCode);
 * ```
 *
 * 3. With purchase amount:
 * ```tsx
 * const handleRedemption = async (qrCode: string, amount: number) => {
 *   try {
 *     const result = await redemption.processQRCode(qrCode, {
 *       purchaseAmount: amount,
 *       notes: `Purchase total: $${amount}`,
 *     });
 *
 *     toast.success('Redemption successful!');
 *   } catch (error) {
 *     toast.error('Redemption failed');
 *   }
 * };
 * ```
 *
 * Anti-patterns to avoid:
 * - DON'T store redemption history in this hook (use useRedemptionState)
 * - DON'T show toasts in this hook (let pages handle UI)
 * - DON'T manage scanner state (page responsibility)
 * - DON'T use React hooks like useState (keep it pure)
 */

// ============================================================================
// EXPORTS
// ============================================================================

export default useRedemptionActions;
